import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import logger from 'src/utils/logger';
import type { components, paths } from '../generated/v1';
import { $api, $apiFetchClient } from './api';
import { hideSpinnerToast, showSpinnerToast } from '../utils/spinnerToaster';

// `has_discrepancy` is exposed by the backend but the OpenAPI types here are
// not yet regenerated — extend locally so the UI can rely on it. Drop the
// intersection once the generated schema includes the field.
export type InboundOrder = components['schemas']['InboundOrder'] & {
  has_discrepancy?: boolean;
  total_units?: number;
  extra_units?: number; // spec 2026-10-19 D
};
export type InboundOrderList = components['schemas']['InboundOrderList'] & {
  has_discrepancy?: boolean;
  total_units?: number;
  extra_units?: number; // spec 2026-10-19 D
};
export type InboundOrderCreate = components['schemas']['InboundOrderCreate'];
// announced: spec 2026-10-16 B; drop the extension once `yarn generate:api-code` picks it up from staging.
// extra_units: spec 2026-10-19 D (same deal as announced).
export type InboundPackage = components['schemas']['InboundPackage'] & { announced?: boolean; extra_units?: number };
export type InboundPackageLines = components['schemas']['InboundPackageExtend'];
export type PackageLines = components['schemas']['PackageLines'];
export type InboundBackorderLine = components['schemas']['InboundBackorderLine'];
export type InboundPhoto = components['schemas']['InboundPhoto'];

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['get', '/stock/inbound'] });
  queryClient.invalidateQueries({ queryKey: ['get', '/stock/inbound/{inbound_order_id}'] });
}

// Inbound cancels for FlowSync-routed orders are REQUESTED at the warehouse (async
// saga) — the mutation response is just an ack, `inbound_state` only flips once the
// WMS confirms (usually within seconds) or stays unchanged on rejection. The
// immediate `invalidate()` above therefore often re-fetches a still-stale state.
// This schedules a single delayed re-invalidation to pick up the confirmation
// without polling or an optimistic update.
export const scheduleInboundRefetch = (client: QueryClient, delayMs = 4000) => {
  window.setTimeout(() => {
    client.invalidateQueries({ queryKey: ['get', '/stock/inbound'] });
    client.invalidateQueries({ queryKey: ['get', '/stock/inbound/{inbound_order_id}'] });
  }, delayMs);
};

export const useInboundOrdersQuery = (
  params: paths['/stock/inbound']['get']['parameters']['query'],
  options?: { enabled?: boolean },
) => {
  return $api.useQuery('get', '/stock/inbound', {
    params: {
      query: params,
    },
    ...options,
  });
};

export const useInboundOrderQuery = (
  params: { path: { inbound_order_id: number }; query?: any },
  options?: { enabled?: boolean },
) => {
  return $api.useQuery('get', `/stock/inbound/{inbound_order_id}`, {
    params,
    ...options,
  });
};

export const useInboundOrderPackageQuery = (
  params: { path: { inbound_order_id: number; package_id: number }; query?: any },
  options?: { enabled?: boolean },
) => {
  return $api.useQuery('get', `/stock/inbound/{inbound_order_id}/package/{package_id}`, {
    params,
    ...options,
  });
};

export const useInboundOrderMutations = () => {
  const queryClient = useQueryClient();

  const createMutation = $api.useMutation('post', '/stock/inbound', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  const cancelMutation = $api.useMutation('put', '/utility/bulk_action/{model}/cancel', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  const duplicateInboundOrders = async (ids: number[]) => {
    const results = await Promise.all(
      ids.map(async id => {
        const { data: order } = await $apiFetchClient.GET('/stock/inbound/{inbound_order_id}', {
          params: { path: { inbound_order_id: id } },
        });
        if (!order) throw new Error(`Failed to fetch inbound order ${id}`);

        const packages = await Promise.all(
          (order.packages ?? [])
            .filter(pkg => pkg.package_id != null)
            .map(async pkg => {
              const { data: pkgDetail } = await $apiFetchClient.GET(
                '/stock/inbound/{inbound_order_id}/package/{package_id}',
                { params: { path: { inbound_order_id: id, package_id: pkg.package_id! } } },
              );
              return {
                package_type: pkg.package_type,
                name: pkg.name,
                lines:
                  pkgDetail?.package_lines?.map(line => ({
                    product_id: line.product.product_id!,
                    quantity: line.quantity,
                    labeling: 'weship' as const,
                    applied_quality_checks:
                      line.applied_quality_checks?.map(qc => qc.id).filter((qcId): qcId is number => qcId != null) ??
                      [],
                    lot_id: line.lot?.id ?? null,
                  })) ?? [],
              };
            }),
        );

        const body: InboundOrderCreate = {
          name: `${order.name} (${t('general.copy')})`,
          date_planned: order.date_planned,
          reference: order.reference || undefined,
          warehouse_id: order.warehouse?.id ?? undefined,
          partner: order.partner
            ? {
                name: order.partner.name,
                company_name: order.partner.company_name,
                address: order.partner.address,
                phone: order.partner.phone,
                email: order.partner.email,
                tax_id: order.partner.tax_id,
                active: true,
                chat: [],
              }
            : undefined,
          packages,
        };

        return createMutation.mutateAsync({ body });
      }),
    );
    invalidate(queryClient);
    return results;
  };

  return {
    createInboundOrder: createMutation.mutate,
    createInboundOrderAsync: createMutation.mutateAsync,
    duplicateInboundOrders,
    cancelMutation,
  };
};

export const useInboundBackorderCancelMutation = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('post', '/stock/inbound/{inbound_order_id}/backorder/cancel', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const getInboundDeliverySlip = async (orderId: number) => {
  showSpinnerToast(t('inbound.get_delivery_slip'));

  try {
    const resp = await $apiFetchClient.GET('/stock/inbound/{inbound_order_id}/pdf/', {
      params: {
        path: {
          inbound_order_id: orderId,
        },
      },
      parseAs: 'blob',
    });

    return resp.data;
  } catch (error) {
    logger.error('Failed to fetch delivery slip PDF', error);
    throw error;
  } finally {
    hideSpinnerToast();
  }
};

export const getInboundPackageDeliverySlip = async (orderId: number, package_id: number) => {
  showSpinnerToast(t('inbound.get_delivery_slip'));

  try {
    const resp = await $apiFetchClient.GET('/stock/inbound/{inbound_order_id}/{package_id}/pdf/', {
      params: {
        path: {
          inbound_order_id: orderId,
          package_id,
        },
      },
      parseAs: 'blob',
    });

    return resp.data;
  } catch (error) {
    logger.error('Failed to fetch delivery slip PDF', error);
    throw error;
  } finally {
    hideSpinnerToast();
  }
};
